import { useEffect, useMemo, useState } from 'react'
import {
  COMPLIANCE_STUDIOS,
  MACHINERY_DIRECTIVE_STUDIO_ID,
  type ComplianceStudio,
} from '@artem-lex/common-server/complianceStudio'
import type { MachineryStandardClass } from '@artem-lex/common-server/standards/iso12100'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CanonicalStandardPickerList } from '@/shared/components/standards/CanonicalStandardPickerList'
import { MachineryStandardTypePicker } from '@/shared/components/standards/MachineryStandardTypePicker'
import {
  useListStandardApplicability,
  useAddStandardApplicability,
  type StandardApplicability,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import type { StandardPickerItem } from '@/shared/hooks/api/standards'
import {
  useListUserStandards,
  useCreateUserStandard,
  useCreateUserStandardFromStandard,
  isManualMachineryStandardType,
  type CreateUserStandardPayload,
} from '@/shared/hooks/api/userStandards'
import { isMachineryListUserStandard } from '@/shared/lib/standardScopeFilters'
import { cn } from '@/shared/lib/utils'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from '@/components/ui/item'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

export type AddStandardModalProps = {
  isOpen: boolean
  onClose: () => void
  complianceId: string
  onAdded?: (applicabilityId: string) => void
  implicitComplianceStudioId?: string
  allowedStandardTypes?: MachineryStandardClass[]
  allowCreateNew?: boolean
  onDuplicateReference?: (existingApplicabilityId: string) => void
}

type MergedItem =
  | {
      source: 'user'
      id: string
      userStandardId: string
      reference: string
      title: string
      type: string
      notInLibrary: false
    }
  | {
      source: 'global'
      id: string
      userStandardId: null
      reference: string
      title: string
      type: string
      notInLibrary: true
    }

function toReferenceLookupKey(reference: string): string {
  return reference.trim()
}

function createEmptyCreateForm(
  implicitComplianceStudioId?: string
): CreateUserStandardPayload {
  return {
    reference: '',
    title: '',
    inEffect: true,
    complianceStudioIds: implicitComplianceStudioId
      ? [implicitComplianceStudioId]
      : [],
  }
}

type CreateStandardContentProps = {
  form: CreateUserStandardPayload
  onFormChange: (form: CreateUserStandardPayload) => void
  complianceStudios: ComplianceStudio[]
  isLoading: boolean
  onToggleComplianceStudio: (complianceStudioId: string) => void
  showComplianceStudios: boolean
  showStandardTypePicker: boolean
  isPending: boolean
}

function CreateStandardContent({
  form,
  onFormChange,
  complianceStudios,
  isLoading,
  onToggleComplianceStudio,
  showComplianceStudios,
  showStandardTypePicker,
  isPending,
}: CreateStandardContentProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')

  if (showComplianceStudios && isLoading) {
    return (
      <div className='flex justify-center py-8'>
        <div className='size-10 animate-spin rounded-full border-2 border-primary border-t-transparent' />
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      <p className='text-sm text-muted-foreground'>
        {t('standardsPage.addStandardModal.createNewDescription')}
      </p>
      <div className='space-y-2'>
        <Label htmlFor='create-new-reference'>
          {t('standardsPage.addStandardModal.createNewReference')} *
        </Label>
        <Input
          id='create-new-reference'
          type='text'
          value={form.reference}
          onChange={(event) =>
            onFormChange({ ...form, reference: event.target.value })
          }
          placeholder={t(
            'standardsPage.addStandardModal.createNewReferencePlaceholder'
          )}
        />
      </div>
      <div className='space-y-2'>
        <Label htmlFor='create-new-title'>
          {t('standardsPage.addStandardModal.createNewTitle')} *
        </Label>
        <Input
          id='create-new-title'
          type='text'
          value={form.title}
          onChange={(event) =>
            onFormChange({ ...form, title: event.target.value })
          }
          placeholder={t(
            'standardsPage.addStandardModal.createNewTitlePlaceholder'
          )}
        />
      </div>
      {showComplianceStudios && (
        <div>
          <span className='mb-2 block text-sm font-medium'>
            {t('standardsPage.addStandardModal.createNewComplianceStudios')} *
          </span>
          <div className='space-y-2'>
            {complianceStudios.map((studio) => (
              <div key={studio.id} className='flex items-center gap-2'>
                <Checkbox
                  id={`create-studio-${studio.id}`}
                  checked={form.complianceStudioIds.includes(studio.id)}
                  onCheckedChange={() => onToggleComplianceStudio(studio.id)}
                />
                <Label
                  htmlFor={`create-studio-${studio.id}`}
                  className='cursor-pointer text-sm font-normal'
                >
                  {studio.title}
                </Label>
              </div>
            ))}
            {complianceStudios.length === 0 && (
              <p className='text-sm text-muted-foreground'>
                {t(
                  'standardsPage.addStandardModal.createNewNoComplianceStudios'
                )}
              </p>
            )}
          </div>
        </div>
      )}
      {showStandardTypePicker && (
        <MachineryStandardTypePicker
          value={form.type}
          onChange={(type) => onFormChange({ ...form, type })}
          disabled={isPending}
          name='compliance-create-standard-type'
        />
      )}
      <div>
        <span className='mb-2 block text-sm font-medium'>
          {t('standardsPage.addStandardModal.createNewStatus')}
        </span>
        <div className='flex flex-col gap-2'>
          <label className='flex items-center gap-2'>
            <input
              type='radio'
              name='create-new-status'
              checked={form.inEffect}
              onChange={() =>
                onFormChange({
                  ...form,
                  inEffect: true,
                  withdrawalOjDate: undefined,
                })
              }
              className='h-4 w-4'
            />
            <span className='text-sm'>
              {t('standardsPage.addStandardModal.createNewStatusCurrent')}
            </span>
          </label>
          <label className='flex items-center gap-2'>
            <input
              type='radio'
              name='create-new-status'
              checked={!form.inEffect}
              onChange={() => onFormChange({ ...form, inEffect: false })}
              className='h-4 w-4'
            />
            <span className='text-sm'>
              {t('standardsPage.addStandardModal.createNewStatusWithdrawn')}
            </span>
          </label>
          {!form.inEffect && (
            <div className='ms-6 mt-2 space-y-2'>
              <Label htmlFor='create-new-withdrawal'>
                {t('standardsPage.addStandardModal.createNewWithdrawalDate')}
              </Label>
              <Input
                id='create-new-withdrawal'
                type='date'
                value={
                  form.withdrawalOjDate
                    ? form.withdrawalOjDate.slice(0, 10)
                    : ''
                }
                onChange={(event) =>
                  onFormChange({
                    ...form,
                    withdrawalOjDate: event.target.value
                      ? event.target.value
                      : null,
                  })
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function AddStandardModal({
  isOpen,
  onClose,
  complianceId,
  onAdded,
  implicitComplianceStudioId,
  allowedStandardTypes,
  allowCreateNew = true,
  onDuplicateReference,
}: AddStandardModalProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const hasImplicitComplianceStudio = implicitComplianceStudioId != null
  const [search, setSearch] = useState('')
  const [selectedItem, setSelectedItem] = useState<MergedItem | null>(null)
  const [modalError, setModalError] = useState<string | null>(null)
  const [createForm, setCreateForm] = useState<CreateUserStandardPayload>(() =>
    createEmptyCreateForm(implicitComplianceStudioId)
  )
  const [activeTab, setActiveTab] = useState<'fromList' | 'createNew'>(
    'fromList'
  )

  const { data: userStandards = [] } = useListUserStandards({ enabled: isOpen })

  const { data: applicabilityList = [] } = useListStandardApplicability(
    isOpen ? complianceId : undefined
  )

  const complianceStudios = COMPLIANCE_STUDIOS

  const addMutation = useAddStandardApplicability(complianceId)
  const createFromStandardMutation = useCreateUserStandardFromStandard()
  const createNewMutation = useCreateUserStandard()
  const implicitCreateType = useMemo((): MachineryStandardClass | null => {
    if (
      !Array.isArray(allowedStandardTypes) ||
      allowedStandardTypes.length !== 1
    ) {
      return null
    }
    return allowedStandardTypes[0]
  }, [allowedStandardTypes])

  const hasMachineryDirectiveStudio = useMemo(() => {
    if (
      hasImplicitComplianceStudio &&
      implicitComplianceStudioId === MACHINERY_DIRECTIVE_STUDIO_ID
    ) {
      return true
    }
    return createForm.complianceStudioIds.includes(
      MACHINERY_DIRECTIVE_STUDIO_ID
    )
  }, [
    hasImplicitComplianceStudio,
    implicitComplianceStudioId,
    createForm.complianceStudioIds,
  ])

  const showStandardTypePicker =
    hasMachineryDirectiveStudio && implicitCreateType == null

  const alreadyAddedIds = useMemo(
    () =>
      new Set(
        applicabilityList
          .map((applicabilityStandard) => applicabilityStandard.userStandardId)
          .filter((id): id is string => id != null)
      ),
    [applicabilityList]
  )
  const allowedTypeSet = useMemo(() => {
    if (
      !Array.isArray(allowedStandardTypes) ||
      allowedStandardTypes.length === 0
    ) {
      return null
    }
    return new Set(
      allowedStandardTypes.map((type) => type.trim().toUpperCase())
    )
  }, [allowedStandardTypes])

  const existingApplicabilityByReference = useMemo(() => {
    const byReference = new Map<string, StandardApplicability>()
    for (const applicabilityStandard of applicabilityList) {
      byReference.set(
        toReferenceLookupKey(applicabilityStandard.reference),
        applicabilityStandard
      )
    }
    return byReference
  }, [applicabilityList])

  const userStandardsList = useMemo((): MergedItem[] => {
    const result: MergedItem[] = []

    for (const userStandard of userStandards) {
      if (!isMachineryListUserStandard(userStandard.type)) {
        continue
      }
      if (
        allowedTypeSet != null &&
        !allowedTypeSet.has((userStandard.type ?? '').trim().toUpperCase())
      ) {
        continue
      }
      if (alreadyAddedIds.has(userStandard.id)) {
        continue
      }
      result.push({
        source: 'user',
        id: userStandard.id,
        userStandardId: userStandard.id,
        reference: userStandard.reference,
        title: userStandard.title,
        type: userStandard.type ?? '',
        notInLibrary: false,
      })
    }
    return result.sort((previousItem, nextItem) =>
      previousItem.reference.localeCompare(nextItem.reference)
    )
  }, [userStandards, alreadyAddedIds, allowedTypeSet])

  const excludeReferences = useMemo(() => {
    const refs = new Set<string>()
    for (const userStandard of userStandards) {
      refs.add(toReferenceLookupKey(userStandard.reference))
    }
    for (const applicabilityStandard of applicabilityList) {
      refs.add(toReferenceLookupKey(applicabilityStandard.reference))
    }
    return refs
  }, [userStandards, applicabilityList])

  const pickerTypes = useMemo(() => {
    if (allowedTypeSet == null) {
      return undefined
    }
    return Array.from(allowedTypeSet)
  }, [allowedTypeSet])

  const searchLower = search.trim().toLowerCase()
  const filteredUserList = useMemo(
    () =>
      searchLower
        ? userStandardsList.filter(
            (item) =>
              item.reference.toLowerCase().includes(searchLower) ||
              item.title.toLowerCase().includes(searchLower)
          )
        : userStandardsList,
    [userStandardsList, searchLower]
  )

  const showUserListAllAdded =
    filteredUserList.length === 0 && userStandardsList.length === 0
  const showUserListNoMatch =
    filteredUserList.length === 0 &&
    userStandardsList.length > 0 &&
    searchLower.length < 2

  const addByUserStandardId = (userStandardId: string) => {
    setModalError(null)
    addMutation.mutate(
      { userStandardId },
      {
        onSuccess: (data) => {
          onAdded?.(data.id)
          toast.success(t('standardsPage.standardAddedSuccess'))
          onClose()
        },
        onError: () => {
          setModalError(t('standardsPage.addStandardModal.addFailed'))
        },
      }
    )
  }

  const handleAddFromList = () => {
    if (!selectedItem) {
      return
    }
    const existing = existingApplicabilityByReference.get(
      toReferenceLookupKey(selectedItem.reference)
    )
    if (existing != null) {
      onDuplicateReference?.(existing.id)
      toast.success(t('standardsPage.addStandardModal.alreadyAddedInfo'))
      onClose()
      return
    }
    setModalError(null)
    if (selectedItem.source === 'user') {
      addByUserStandardId(selectedItem.userStandardId)
      return
    }
    createFromStandardMutation.mutate(
      { sourceStandardId: selectedItem.id },
      {
        onSuccess: (created) => {
          addByUserStandardId(created.id)
        },
        onError: () => {
          setModalError(t('standardsPage.addStandardModal.createAndAddFailed'))
        },
      }
    )
  }

  const handleCreateNew = () => {
    setModalError(null)
    const reference = createForm.reference.trim()
    const title = createForm.title.trim()
    if (!reference || !title) {
      setModalError(t('standardsPage.addStandardModal.createNewValidation'))
      return
    }
    if (
      !hasImplicitComplianceStudio &&
      createForm.complianceStudioIds.length === 0
    ) {
      setModalError(
        t('standardsPage.addStandardModal.createNewComplianceStudioRequired')
      )
      return
    }
    const complianceStudioIds = hasImplicitComplianceStudio
      ? [implicitComplianceStudioId]
      : createForm.complianceStudioIds
    let createType: CreateUserStandardPayload['type'] | undefined
    if (isManualMachineryStandardType(implicitCreateType)) {
      createType = implicitCreateType
    } else if (
      hasMachineryDirectiveStudio &&
      isManualMachineryStandardType(createForm.type)
    ) {
      createType = createForm.type
    }
    const payload: Parameters<typeof createNewMutation.mutate>[0] = {
      reference,
      title,
      inEffect: createForm.inEffect,
      complianceStudioIds,
      withdrawalOjDate: createForm.withdrawalOjDate?.trim() || null,
    }
    if (createType != null) {
      payload.type = createType
    }
    createNewMutation.mutate(payload, {
      onSuccess: (created) => {
        addByUserStandardId(created.id)
      },
      onError: () => {
        setModalError(t('standardsPage.addStandardModal.createNewFailed'))
      },
    })
  }

  const handleClose = () => {
    setSearch('')
    setSelectedItem(null)
    setModalError(null)
    setCreateForm(createEmptyCreateForm(implicitComplianceStudioId))
    setActiveTab('fromList')
    onClose()
  }

  const handleSelectUserItem = (item: MergedItem) => {
    setSelectedItem(item)
  }

  const handleSelectCanonicalItem = (item: StandardPickerItem) => {
    setSelectedItem({
      source: 'global',
      id: item.id,
      userStandardId: null,
      reference: item.reference,
      title: item.title,
      type: item.type,
      notInLibrary: true,
    })
  }

  const toggleComplianceStudio = (complianceStudioId: string) => {
    setCreateForm((prev) => {
      const isSelected = prev.complianceStudioIds.includes(complianceStudioId)
      const nextComplianceStudioIds = isSelected
        ? prev.complianceStudioIds.filter((id) => id !== complianceStudioId)
        : [...prev.complianceStudioIds, complianceStudioId]
      const stillHasMachineryDirective = nextComplianceStudioIds.includes(
        MACHINERY_DIRECTIVE_STUDIO_ID
      )
      return {
        ...prev,
        complianceStudioIds: nextComplianceStudioIds,
        type: stillHasMachineryDirective ? prev.type : undefined,
      }
    })
  }

  const isPending =
    addMutation.isPending ||
    createFromStandardMutation.isPending ||
    createNewMutation.isPending

  const listButtonLabel = selectedItem?.notInLibrary
    ? t('standardsPage.addStandardModal.createAndAddButton')
    : t('common:actions.add')

  const errorMessage = modalError

  const isCreateButtonDisabled =
    isPending ||
    !createForm.reference.trim() ||
    !createForm.title.trim() ||
    (!hasImplicitComplianceStudio &&
      createForm.complianceStudioIds.length === 0)

  const showCreateNewTab = allowCreateNew

  useEffect(() => {
    if (!showCreateNewTab && activeTab !== 'fromList') {
      setActiveTab('fromList')
    }
  }, [showCreateNewTab, activeTab])

  return (
    <Sheet
      modal={false}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose()
        }
      }}
    >
      <SheetContent
        side='right'
        showOverlay={false}
        className='flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl'
      >
        <div className='flex min-h-0 flex-1 flex-col'>
          <SheetHeader className='shrink-0 border-b px-6 py-4 pe-12 text-start'>
            <SheetTitle>{t('standardsPage.addStandardModal.title')}</SheetTitle>
          </SheetHeader>

          <div className='flex min-h-0 flex-1 flex-col px-6 py-4'>
            <Tabs
              value={activeTab}
              onValueChange={(value) =>
                setActiveTab(value as 'fromList' | 'createNew')
              }
              className='flex min-h-0 flex-1 flex-col'
            >
              <TabsList
                className={cn(
                  'grid w-full shrink-0',
                  showCreateNewTab ? 'grid-cols-2' : 'grid-cols-1'
                )}
              >
                <TabsTrigger value='fromList'>
                  {t('standardsPage.addStandardModal.tabFromList')}
                </TabsTrigger>
                {showCreateNewTab && (
                  <TabsTrigger value='createNew'>
                    {t('standardsPage.addStandardModal.tabCreateNew')}
                  </TabsTrigger>
                )}
              </TabsList>

              {errorMessage != null && (
                <Alert variant='destructive' className='mt-2 shrink-0'>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              <TabsContent
                value='fromList'
                className='mt-4 flex min-h-0 flex-1 flex-col gap-4 overflow-hidden'
              >
                <div className='space-y-2'>
                  <Label htmlFor='add-standard-search'>
                    {t('standardsPage.addStandardModal.label')}
                  </Label>
                  <Input
                    id='add-standard-search'
                    type='text'
                    placeholder={t(
                      'standardsPage.addStandardModal.searchPlaceholder'
                    )}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>

                <div className='min-h-0 flex-1 overflow-y-auto'>
                  {filteredUserList.length > 0 ? (
                    <ItemGroup>
                      {filteredUserList.map((item) => (
                        <Item
                          key={`${item.source}-${item.id}`}
                          variant='muted'
                          className='hover:bg-muted/70'
                          render={
                            <button
                              type='button'
                              onClick={() => handleSelectUserItem(item)}
                              disabled={isPending}
                              className={cn(
                                selectedItem?.id === item.id &&
                                  selectedItem?.source === item.source &&
                                  'ring-2 ring-primary ring-inset'
                              )}
                            />
                          }
                        >
                          <ItemContent>
                            <div className='flex flex-wrap items-center gap-2'>
                              <ItemTitle>{item.reference}</ItemTitle>
                            </div>
                            <ItemDescription className='line-clamp-2'>
                              {item.title}
                            </ItemDescription>
                            {item.type ? (
                              <ItemDescription className='text-xs'>
                                {t('standardsPage.addStandardModal.typeLabel', {
                                  type: item.type,
                                })}
                              </ItemDescription>
                            ) : null}
                          </ItemContent>
                        </Item>
                      ))}
                    </ItemGroup>
                  ) : (
                    <>
                      {showUserListAllAdded && (
                        <p className='text-sm text-muted-foreground'>
                          {t('standardsPage.addStandardModal.allAdded')}
                        </p>
                      )}
                      {showUserListNoMatch && (
                        <p className='text-sm text-muted-foreground'>
                          {t('standardsPage.addStandardModal.noMatch')}
                        </p>
                      )}
                    </>
                  )}

                  {filteredUserList.length > 0 && (
                    <div
                      className='my-3 border-t'
                      role='separator'
                      aria-hidden='true'
                    />
                  )}

                  <CanonicalStandardPickerList
                    enabled={isOpen}
                    search={search}
                    types={pickerTypes ?? ['A', 'B', 'C']}
                    excludeReferences={excludeReferences}
                    selectedId={
                      selectedItem?.source === 'global' ? selectedItem.id : null
                    }
                    onSelect={handleSelectCanonicalItem}
                    renderBadge={() => (
                      <Badge
                        variant='outline'
                        className='border-muted-foreground/40 bg-muted text-muted-foreground'
                      >
                        {t('standardsPage.addStandardModal.notInLibrary')}
                      </Badge>
                    )}
                    renderTypeLabel={(type) =>
                      t('standardsPage.addStandardModal.typeLabel', { type })
                    }
                    disabled={isPending}
                  />
                </div>
              </TabsContent>

              {showCreateNewTab && (
                <TabsContent
                  value='createNew'
                  className='mt-4 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto'
                >
                  <CreateStandardContent
                    form={createForm}
                    onFormChange={setCreateForm}
                    complianceStudios={complianceStudios}
                    isLoading={false}
                    onToggleComplianceStudio={toggleComplianceStudio}
                    showComplianceStudios={!hasImplicitComplianceStudio}
                    showStandardTypePicker={showStandardTypePicker}
                    isPending={isPending}
                  />
                </TabsContent>
              )}
            </Tabs>
          </div>

          <SheetFooter className='shrink-0 border-t px-6 py-4 sm:flex-row sm:justify-end'>
            <Button
              variant='outline'
              onClick={handleClose}
              disabled={isPending}
            >
              {t('standardsPage.addStandardModal.close')}
            </Button>
            {activeTab === 'fromList' || !showCreateNewTab ? (
              <Button
                onClick={handleAddFromList}
                disabled={!selectedItem || isPending}
              >
                {listButtonLabel}
              </Button>
            ) : (
              <Button
                onClick={handleCreateNew}
                disabled={isCreateButtonDisabled}
              >
                {t('standardsPage.addStandardModal.createAndAddButton')}
              </Button>
            )}
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  )
}
